import React, { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { sendWebhook } from '../utils/webhook';
import { Helmet } from 'react-helmet-async';

export default function VisitorTracker() {
  const location = useLocation();
  const currentPath = location.pathname;

  // Define page-specific SEO metadata
  const getPageMetadata = () => {
    switch (currentPath) {
      case '/':
        return {
          title: 'DS Marketing - Premier Content Creation & Digital Marketing Agency',
          description: 'Transform your brand with our ROI-focused content creation, email marketing, and brand design services. Guaranteed results.',
          keywords: 'content marketing, email marketing, brand design, digital marketing agency, content creation, marketing ROI'
        };
      case '/services':
        return {
          title: 'Marketing Services | Content Creation, Email Marketing, Brand Design | DS Marketing',
          description: 'Explore our comprehensive marketing services including content creation, email marketing, brand design, analytics, and creative strategy.',
          keywords: 'marketing services, content creation, email marketing, brand design, analytics, creative strategy'
        };
      case '/work':
        return {
          title: 'Our Portfolio | Marketing Case Studies & Success Stories | DS Marketing',
          description: 'View our portfolio of successful marketing campaigns and content strategies that have delivered measurable results for our clients.',
          keywords: 'marketing portfolio, case studies, content strategy, marketing success stories, brand transformation'
        };
      case '/about':
        return {
          title: 'About DS Marketing | Our Story, Values & Team | Content Marketing Experts',
          description: 'Learn about DS Marketing\'s journey, our values of excellence and innovation, and meet the team behind our award-winning content strategies.',
          keywords: 'marketing agency, content experts, marketing team, agency values, marketing excellence'
        };
      case '/blog':
        return {
          title: 'Marketing Blog | Content Strategy, Digital Marketing Tips | DS Marketing',
          description: 'Expert insights on content creation, digital marketing strategies, brand development, and industry trends to help grow your business.',
          keywords: 'marketing blog, content strategy, digital marketing tips, brand development, industry trends'
        };
      case '/contact':
        return {
          title: 'Contact DS Marketing | Get a Custom Marketing Strategy | Free Consultation',
          description: 'Ready to transform your marketing? Contact DS Marketing for a personalized strategy and discover how we can help achieve your business goals.',
          keywords: 'contact marketing agency, marketing consultation, content strategy, marketing services'
        };
      case '/schedule':
        return {
          title: 'Schedule a Free Strategy Call | Marketing Consultation | DS Marketing',
          description: 'Book your free 30-minute marketing strategy session with our experts and discover how to accelerate your growth with proven content strategies.',
          keywords: 'marketing strategy call, free consultation, content strategy, marketing advice'
        };
      default:
        return {
          title: 'DS Marketing - Premier Content Creation & Digital Marketing Agency',
          description: 'Transform your brand with our ROI-focused content creation, email marketing, and brand design services. Guaranteed results.',
          keywords: 'content marketing, email marketing, brand design, digital marketing agency, content creation, marketing ROI'
        };
    }
  };

  const metadata = getPageMetadata();

  const sendVisitorInfo = useCallback(async () => {
    try {
      // Only track if this is a new session or the page has changed
      const currentPath = location.pathname;
      const lastPath = sessionStorage.getItem('lastPath');
      const lastVisitTime = sessionStorage.getItem('lastVisitTime');
      const now = Date.now();
      
      // Check if we should track this visit (new session or 5 minutes passed)
      if (
        lastPath === currentPath && 
        lastVisitTime && 
        now - parseInt(lastVisitTime) < 300000 // 5 minutes
      ) {
        return;
      }

      // Generate a unique session ID if not exists
      const sessionId = sessionStorage.getItem('visitorSessionId') || 
        `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      const visitorInfo = {
        type: 'visitor',
        timestamp: new Date().toISOString(),
        page: currentPath,
        referrer: document.referrer || 'Direct',
        userAgent: navigator.userAgent,
        language: navigator.language,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        emailTo: 'pascoalapolinario246@gmail.com',
        subject: 'New Website Visitor',
        sessionId,
        isNewVisitor: !localStorage.getItem('returningVisitor'),
        lastVisit: localStorage.getItem('lastVisit') || null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        platform: navigator.platform,
        vendor: navigator.vendor,
        viewportSize: `${window.innerWidth}x${window.innerHeight}`
      };

      // Update session and visitor tracking
      try {
        sessionStorage.setItem('visitorSessionId', sessionId);
        sessionStorage.setItem('lastPath', currentPath);
        sessionStorage.setItem('lastVisitTime', now.toString());
        localStorage.setItem('returningVisitor', 'true');
        localStorage.setItem('lastVisit', new Date().toISOString());
      } catch (storageError) {
        console.error('Storage error:', storageError);
      }

      // Send the webhook with retry logic
      let retries = 2;
      let delay = 1000;

      while (retries >= 0) {
        try {
          const success = await sendWebhook(visitorInfo);
          if (success) break;
          
          retries--;
          if (retries >= 0) {
            await new Promise(resolve => setTimeout(resolve, delay));
            delay *= 2; // Exponential backoff
          }
        } catch (webhookError) {
          console.error(`Webhook attempt failed (${retries} retries left):`, webhookError);
          retries--;
          if (retries >= 0) {
            await new Promise(resolve => setTimeout(resolve, delay));
            delay *= 2;
          }
        }
      }
    } catch (error) {
      console.error('Visitor tracking error:', error);
    }
  }, [location.pathname]);

  useEffect(() => {
    // Debounce the tracking to prevent multiple rapid calls
    const timeoutId = setTimeout(sendVisitorInfo, 1500);
    return () => clearTimeout(timeoutId);
  }, [sendVisitorInfo]);

  return (
    <Helmet>
      <title>{metadata.title}</title>
      <meta name="description" content={metadata.description} />
      <meta name="keywords" content={metadata.keywords} />
      <link rel="canonical" href={`https://dsmarketing.fr${currentPath}`} />
      <meta property="og:url" content={`https://dsmarketing.fr${currentPath}`} />
      <meta property="og:title" content={metadata.title} />
      <meta property="og:description" content={metadata.description} />
    </Helmet>
  );
}